import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  CheckboxGroup
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import DayOfMonth from '../day';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Timezone from '../timezone';
import { CardBlock } from '../../card';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import { IEpxSDOHSchedule } from '../../../../libs/apps/epxsdoh';

interface EpxSDOHModule {
  key: string;
  mode: 'month' | 'year';
  isActive: boolean;
}

interface EpxSDOHData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  times: string[];
  daysOfMonth: number[];
  date?: string;
  modules: Record<string, EpxSDOHModule>;
}

type EpxSDOHDataRaw = Omit<
  DateSerialized<EpxSDOHData, 'start' | 'end'>,
  'modules' | 'daysOfMonth'
> & {
  daysOfMonth: string[];
  modules: string[];
};

export const EpxSDOHDefaultData: EpxSDOHData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  times: ['10:00'],
  daysOfMonth: [1],
  modules: {
    food: {
      key: 'food',
      mode: 'month',
      isActive: true
    },
    housing: {
      key: 'housing',
      mode: 'month',
      isActive: true
    },
    transportation: {
      key: 'transportation',
      mode: 'month',
      isActive: true
    }
  }
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

const MODULE_OPTIONS = [
  {
    value: 'screening',
    label: 'Screening Mode'
  },
  {
    value: 'housing',
    label: 'Housing'
  },
  {
    value: 'food',
    label: 'Food'
  },
  {
    value: 'transportation',
    label: 'Transportation'
  },
  {
    value: 'utilities',
    label: 'Utilities'
  },
  {
    value: 'safety',
    label: 'Safety'
  },
  {
    value: 'support',
    label: 'Support'
  },
  {
    value: 'employment',
    label: 'Employment'
  },
  {
    value: 'healthLiteracy',
    label: 'Health Literacy'
  },
  {
    value: 'financialStrain',
    label: 'Financial Strain'
  },
  {
    value: 'insurance',
    label: 'Health Insurance'
  }
];

interface EpxSDOHFormProps {
  setForm?: (form: FormInstance<EpxSDOHDataRaw>) => void;
  onSubmit?: (data: EpxSDOHData) => void;
  rawSchedule?: IEpxSDOHSchedule;
  isDisabled?: boolean;
}

const EpxSDOHForm = ({
  setForm,
  onSubmit: _onSubmit,
  rawSchedule,
  isDisabled
}: EpxSDOHFormProps): JSX.Element => {
  const serialize = (data: EpxSDOHDataRaw): EpxSDOHData => {
    const modules: Record<string, EpxSDOHModule> = {};
    let hasScreening = false;
    for (const key of data.modules) {
      const mode = key === 'screening' ? 'year' : 'month';
      if (key === 'screening') {
        hasScreening = true;
      }
      modules[key] = {
        key,
        mode,
        isActive: true
      };
    }
    if (rawSchedule) {
      for (const key of Object.keys(rawSchedule.modules)) {
        const module = rawSchedule.modules[key];
        if (!modules[key]) {
          modules[key] = {
            key: key,
            mode: module.mode,
            isActive: false
          };
        }
      }
    }
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, 1),
      daysOfMonth: data.daysOfMonth.map((day) => parseInt(day)),
      date: hasScreening ? SerializeDate(data.start, 'MM/DD') : null,
      modules
    };
  };
  const deserialize = (data: EpxSDOHData): EpxSDOHDataRaw => {
    const modules: string[] = [];
    for (const key of Object.keys(data.modules)) {
      if (data.modules[key].isActive) {
        modules.push(key);
      }
    }
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT),
      daysOfMonth: data.daysOfMonth.map((day) => day.toString()),
      modules
    };
  };
  const defaultData = deserialize(EpxSDOHDefaultData);
  const form = new FormInstance<EpxSDOHDataRaw, EpxSDOHData>(defaultData, {
    serialize,
    deserialize
  });
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxSDOHData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <SelectMethod />
            </Col>
            <Col sm={12}>
              <SelectLanguage />
            </Col>
          </Row>
        </CardBlock>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <StartDate label="Start Date" name="start" fill />
            </Col>
            <Col sm={12}>
              <EndDate label="End Date" name="end" />
            </Col>
            <Col sm={12}>
              <SelectTime label="Message At" name="times[0]" />
            </Col>
            <Col sm={12}>
              <Timezone label="Timezone" name="tz" fill />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <DayOfMonth
                label="Day of Month for Messages"
                name="daysOfMonth[0]"
                fill
              />
            </Col>
          </Row>
        </CardBlock>
        <CardBlock>
          <Row>
            <Col sm={24}>
              <CheckboxGroup
                label="Choose Social Determinant Modules"
                name="modules"
                options={MODULE_OPTIONS}
              />
            </Col>
          </Row>
        </CardBlock>
      </Form>
    </CardBlock>
  );
};

export default EpxSDOHForm;
